import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLandingData } from "../../../hooks/useLandingData";
import LiveDemoAnimation from "./LiveDemoAnimation";

export default function DashboardSection() {
  const { dashboardStats, recentCalls } = useLandingData();
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<"dashboard" | "demo">("demo");
  const [demoKey, setDemoKey] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const chartData = [42, 58, 45, 72, 65, 88, 76, 94, 85, 100, 88, 96];
  const chartMonths = ["E", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

  const features = [
    { icon: "ri-pencil-line", title: t("dash_f1_title"), desc: t("dash_f1_desc") },
    { icon: "ri-calendar-check-2-line", title: t("dash_f2_title"), desc: t("dash_f2_desc") },
    { icon: "ri-user-voice-line", title: t("dash_f3_title"), desc: t("dash_f3_desc") },
  ];

  return (
    <section id="dashboard" ref={ref} className="py-28 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div
            className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
          >
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">
              {t("dash_label")}
            </span>
            <h2
              className="text-4xl lg:text-5xl font-bold text-stone-900 leading-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("dash_title_1")}
              <br />
              <span className="text-amber-500">{t("dash_title_2")}</span>
            </h2>
            <p className="text-stone-500 text-lg leading-relaxed mb-8">
              {t("dash_subtitle")}
            </p>

            <div className="space-y-4 mb-8">
              {features.map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50 border border-amber-100 flex-shrink-0">
                    <i className={`${item.icon} text-amber-600 text-lg`} />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-800 text-sm mb-1">{item.title}</p>
                    <p className="text-stone-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
          >
            {/* Tab switcher */}
            <div className="flex items-center gap-1 bg-stone-100 rounded-full p-1 mb-5 w-fit">
              <button
                onClick={() => setActiveTab("demo")}
                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer ${
                  activeTab === "demo"
                    ? "bg-white text-stone-900 shadow-sm"
                    : "text-stone-400 hover:text-stone-600"
                }`}
              >
                <i className="ri-play-circle-line mr-1.5" />
                {t("dash_tab_demo")}
              </button>
              <button
                onClick={() => setActiveTab("dashboard")}
                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer ${
                  activeTab === "dashboard"
                    ? "bg-white text-stone-900 shadow-sm"
                    : "text-stone-400 hover:text-stone-600"
                }`}
              >
                <i className="ri-dashboard-line mr-1.5" />
                {t("dash_tab_dashboard")}
              </button>
            </div>

            {activeTab === "demo" ? (
              <div>
                <LiveDemoAnimation key={demoKey} active={activeTab === "demo"} />
                <button
                  onClick={() => setDemoKey((k) => k + 1)}
                  className="whitespace-nowrap mt-3 w-full flex items-center justify-center gap-2 text-xs text-stone-400 hover:text-amber-600 transition-colors cursor-pointer py-2"
                >
                  <i className="ri-refresh-line" />
                  {t("dash_repeat_demo")}
                </button>
              </div>
            ) : (
              <div className="bg-white border border-stone-100 rounded-3xl overflow-hidden">
                <div className="bg-stone-900 px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src="https://static.readdy.ai/image/464d6b2d88c2c0b329c43db37f7a6b21/5e3d82f418168fbdbc49efd95d2ee821.png"
                      alt="VoiceAI"
                      className="h-6 w-auto"
                    />
                    <span className="text-white/60 text-xs">{t("dash_client_label")}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  </div>
                </div>

                <div className="p-5">
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {dashboardStats.map((s) => (
                      <div key={s.label} className="bg-stone-50 rounded-2xl p-4">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-stone-400 text-xs">{s.label}</span>
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className={`${s.icon} text-amber-500 text-sm`} />
                          </div>
                        </div>
                        <p className="text-xl font-black text-stone-900">{s.value}</p>
                        <p className={`text-xs font-semibold mt-0.5 ${s.positive ? "text-emerald-500" : "text-red-500"}`}>
                          {s.change} {t("dash_vs_prev")}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-stone-50 rounded-2xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-stone-700 text-xs font-semibold">{t("dash_calls_title")}</span>
                      <span className="text-amber-600 text-xs font-semibold">{t("dash_calls_growth")}</span>
                    </div>
                    <div className="flex items-end gap-1.5 h-16">
                      {chartData.map((val, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <div
                            className="w-full rounded-sm transition-all duration-500"
                            style={{
                              height: `${visible ? (val / 100) * 56 : 4}px`,
                              backgroundColor: i === chartData.length - 1 ? "#F59E0B" : "#E5E7EB",
                              transitionDelay: `${300 + i * 40}ms`,
                            }}
                          />
                          <span className="text-stone-300 text-xs">{chartMonths[i]}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-stone-50 rounded-2xl p-4">
                    <p className="text-stone-700 text-xs font-semibold mb-3">{t("dash_recent_calls")}</p>
                    <div className="space-y-2">
                      {recentCalls.slice(0, 3).map((call, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold px-1.5 py-0.5 bg-stone-200 text-stone-600 rounded">{call.lang}</span>
                            <span className="text-stone-600 text-xs">{call.caller}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-stone-400 text-xs">{call.duration}</span>
                            <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full border border-emerald-100">
                              {call.outcome}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
